import DOMPurify from 'dompurify';
import Image from './AppImage';
import Icon from './AppIcon';

const parseBlockValue = (value, type) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (type === 'image') return parsed?.url || value;
    if (type === 'link') return parsed;
    return value;
  } catch {
    return type === 'link' ? { url: value, text: value } : value;
  }
};

const ALLOWED_TAGS = ['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'strong', 'em', 'u', 's', 'a', 'br'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

/**
 * Renders a single content block (text, image, audio, link).
 * Used by article-reading and discussion-circle to avoid raw HTML display.
 */
export const renderContentBlock = (block, { ImageComponent = Image } = {}) => {
  const type = (block.type || '').toLowerCase();
  switch (type) {
    case 'text':
      return (
        <div
          key={block.sortOrder}
          className="mb-8 prose prose-lg max-w-none"
          style={{
            fontFamily: 'Amiri, serif',
            fontSize: '22px',
            lineHeight: '2.0',
            color: '#2C2B29',
            direction: 'rtl',
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(block.value || '', {
              ALLOWED_TAGS,
              ALLOWED_ATTR,
            }),
          }}
        />
      );

    case 'image': {
      const src = parseBlockValue(block.value, 'image');
      if (!src) return null;
      let align = 'center';
      let caption = null;
      let width = null;
      let height = null;
      let rotate = 0;
      try {
        const parsed = JSON.parse(block.value);
        align = parsed?.align || 'center';
        caption = parsed?.caption || null;
        width = parsed?.width || null;
        height = parsed?.height || null;
        rotate = parsed?.rotate ?? 0;
      } catch {}
      const imgStyle = { maxWidth: '100%' };
      if (width) imgStyle.width = `${width}px`;
      if (height) imgStyle.height = `${height}px`;
      if (rotate) imgStyle.transform = `rotate(${rotate}deg)`;
      return (
        <figure
          key={block.sortOrder}
          className="my-10"
          style={{ textAlign: align === 'left' ? 'left' : align === 'right' ? 'right' : 'center' }}
        >
          <ImageComponent
            src={src}
            alt={caption || ''}
            className="rounded-xl max-w-full h-auto shadow-md"
            style={Object.keys(imgStyle).length ? imgStyle : undefined}
          />
          {caption && (
            <figcaption
              className="mt-2 text-gray-500 text-center"
              style={{ fontFamily: 'Amiri, serif', fontSize: '1.05rem' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(caption, {
                  ALLOWED_TAGS: ['strong', 'em', 'u', 's', 'a', 'br'],
                  ALLOWED_ATTR,
                }),
              }}
            />
          )}
        </figure>
      );
    }

    case 'audio': {
      const src = parseBlockValue(block.value, 'audio');
      if (!src) return null;
      return (
        <div key={block.sortOrder} className="my-10 flex justify-center">
          <audio
            controls
            src={src}
            className="w-full max-w-lg"
            style={{ direction: 'ltr' }}
          >
            متصفحك لا يدعم تشغيل الصوت.
          </audio>
        </div>
      );
    }

    case 'link': {
      const linkData = parseBlockValue(block.value, 'link');
      const href = linkData?.url || linkData;
      const text = linkData?.text || linkData || href;
      if (!href) return null;
      return (
        <div key={block.sortOrder} className="my-6 text-center">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 hover:shadow-md"
            style={{
              fontFamily: 'Amiri, serif',
              fontSize: '18px',
              color: '#C5A059',
              backgroundColor: 'rgba(197, 160, 89, 0.08)',
              border: '1px solid rgba(197, 160, 89, 0.25)',
            }}
          >
            <Icon name="ExternalLink" size={18} color="#C5A059" />
            <span>{text}</span>
          </a>
        </div>
      );
    }

    default:
      return null;
  }
};

/**
 * Renders all content blocks sorted by sortOrder.
 */
export const ContentBlocks = ({ content = [], ImageComponent }) => {
  const sorted = [...content].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return (
    <div className="prose prose-lg max-w-none">
      {sorted.map((block) => renderContentBlock(block, { ImageComponent }))}
    </div>
  );
};

export default ContentBlocks;
